"use client";

import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import Button from "@/components/static/Button";
import ArrowRight_Icon from "@/icons/ArrowRight";
import parse from "html-react-parser/lib/index";
import { cn } from "@/lib/utils";
import { RxChevronDown } from "react-icons/rx";
import { AnimatePresence, motion, useScroll, useMotionValueEvent } from "framer-motion";
import { NavLinkProps, OptionsProps } from "@/lib/types";
import Image from "next/image";
import Link, { LinkProps } from "next/link";
import { languages } from "@/i18n/config";

export default function InnerNavbar({
    navMenu,
    isOverlay,
    options,
    translations,
    locale,
}: {
    navMenu: any;
    isOverlay: boolean;
    options: OptionsProps;
    translations: { de: string; en: string; fr: string };
    locale: string;
}) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const isMobile = useMediaQuery({ query: "(max-width: 992px)" });
    const { scrollY } = useScroll();
    const [headerPos, setHeaderPos] = useState(0);
    const [langOpen, setLangOpen] = useState(false);

    useMotionValueEvent(scrollY, "change", (currentPos) => {
        setHeaderPos(currentPos);
    });

    const scrollDistance = 10;
    const navStyles =
        headerPos >= scrollDistance || !isOverlay
            ? "bg-white bg-opacity-80 backdrop:blur-xl"
            : "bg-white lg:bg-transparent text-black";
    return (
        <motion.nav
            className={cn(
                "flex fixed w-full items-center z-30 transition py-2 lg:py-4",
                navStyles,
                headerPos >= scrollDistance && "border-b"
            )}
        >
            <div className='container lg:flex lg:items-center lg:justify-between'>
                <div className='flex items-center justify-between'>
                    <a href='/'>
                        <Image
                            src={options?.logo?.url ?? "#"}
                            height='70'
                            width='114'
                            alt='Men Logo'
                            className='max-lg:w-20 w-24'
                        />
                    </a>
                    <div className='flex items-center gap-4 '>
                        <button
                            className='-mr-2 flex size-12 flex-col items-center justify-center lg:hidden'
                            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                        >
                            <motion.span
                                className='my-[3px] h-0.5 w-6 bg-primary'
                                animate={isMobileMenuOpen ? ["open", "rotatePhase"] : "closed"}
                            />
                            <motion.span
                                className='my-[3px] h-0.5 w-6 bg-primary'
                                animate={isMobileMenuOpen ? "open" : "closed"}
                            />
                            <motion.span
                                className='my-[3px] h-0.5 w-6 bg-primary'
                                animate={isMobileMenuOpen ? ["open", "rotatePhase"] : "closed"}
                            />
                        </button>
                        <div className='lg:hidden'>
                            <div className='relative'>
                                <button
                                    className={cn(
                                        "uppercase flex items-center gap-x-2 text-black bg-[#EAECEFCC] px-3 py-2 rounded-[50px]"
                                        // headerPos >= scrollDistance && "text-black"
                                    )}
                                    onClick={() => {
                                        setLangOpen(!langOpen);
                                    }}
                                >
                                    <div className='rounded-full overflow-hidden'>
                                        {parse(
                                            languages.find((lang) => lang.locale === locale)
                                                ?.icon || ""
                                        )}
                                    </div>
                                    {locale}
                                    <RxChevronDown />
                                </button>
                                {langOpen && (
                                    <div className='absolute top-full left-0 bg-[#EAECEF] rounded-lg overflow-hidden mt-3'>
                                        <ul>
                                            {languages.map((language, index) => {
                                                return (
                                                    <li key={index}>
                                                        <Link
                                                            lang={language.locale}
                                                            href={
                                                                translations[
                                                                    language.locale as keyof typeof translations
                                                                ]
                                                            }
                                                            className='p-4 block leading-none hover:bg-black/10'
                                                        >
                                                            {language.name}
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <motion.div
                    variants={{
                        open: {
                            height: "var(--height-open, 94dvh)",
                        },
                        close: {
                            height: "var(--height-closed, 0)",
                        },
                    }}
                    initial='close'
                    exit='close'
                    animate={isMobileMenuOpen ? "open" : "close"}
                    transition={{ duration: 0.4 }}
                    className={cn(
                        "px-[5%] transition duration-500 text-[150%] overflow-y-scroll lg:text-inherit lg:mt-0 lg:overflow-visible lg:flex lg:items-center lg:p-0 lg:[--height-closed:auto] lg:[--height-open:auto] bg-[#eaecef]  lg:bg-[#EAECEFCC] rounded-[18px]",
                        isMobileMenuOpen && "translate-y-4"
                    )}
                >
                    {navMenu?.menu_items?.map((navLink: any, index: number) => {
                        const isLast = index === navMenu.menu_items.length - 1;
                        return (
                            <div
                                key={index}
                                className={cn(
                                    "lg:border-r lg:last:border-0 lg:border-stroke lg:first:pr-4 lg:px-4 lg:text-md",
                                    navLink.classes.at(0) === "small" && "relative"
                                )}
                            >
                                {navLink.menu_children && navLink.menu_children.length > 0 ? (
                                    <SubMenu
                                        navLink={navLink}
                                        options={options}
                                        isMobile={isMobile}
                                    />
                                ) : isLast ? (
                                    <Link
                                        href={navLink.url ?? ""}
                                        className='block text-[150%] lg:text-md py-5 lg:py-4 lg:lg:px-4'
                                    >
                                        <div className='flex items-center gap-2'>
                                            {parse(navLink.title)}
                                            <ArrowRight_Icon className='text-brand-dark size-5' />
                                        </div>
                                    </Link>
                                ) : (
                                    <Link
                                        href={navLink.url ?? ""}
                                        className='text-[150%] lg:text-md flex w-full items-center py-5 lg:py-0 justify-between gap-2 text-left lg:flex-none lg:justify-start lg:px-4'
                                    >
                                        {parse(navLink.title)}
                                    </Link>
                                )}
                            </div>
                        );
                    })}
                </motion.div>
                <div className='hidden lg:block'>
                    <div className='relative'>
                        <button
                            className={cn(
                                "uppercase flex items-center gap-x-2 text-black bg-[#EAECEFCC] px-3 py-2 rounded-[50px]"
                                // headerPos >= scrollDistance && "text-black"
                            )}
                            onClick={() => {
                                setLangOpen(!langOpen);
                            }}
                        >
                            <div className='rounded-full overflow-hidden'>
                                {parse(
                                    languages.find((lang) => lang.locale === locale)?.icon || ""
                                )}
                            </div>
                            {locale}
                            <RxChevronDown />
                        </button>
                        {langOpen && (
                            <div className='absolute top-full left-0 bg-[#EAECEF] rounded-lg overflow-hidden mt-3'>
                                <ul>
                                    {languages.map((language, index) => {
                                        return (
                                            <li key={index}>
                                                <Link
                                                    lang={language.locale}
                                                    href={
                                                        translations[
                                                            language.locale as keyof typeof translations
                                                        ]
                                                    }
                                                    className='p-4 block leading-none hover:bg-black/10'
                                                >
                                                    {language.name}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </motion.nav>
    );
}

const SubMenu = ({
    navLink,
    options,
    isMobile,
}: {
    navLink: NavLinkProps;
    options: OptionsProps;
    isMobile: boolean;
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const smallNav = navLink.classes.at(0);

    useEffect(() => {
        function closeFilter(event: MouseEvent) {
            if (
                !isMobile &&
                isDropdownOpen &&
                !event.composedPath().some((el) => (el as HTMLElement).classList?.contains("nav"))
            ) {
                setIsDropdownOpen(false);
            }
        }

        document.addEventListener("click", closeFilter);

        return () => {
            document.removeEventListener("click", closeFilter);
        };
    }, [isDropdownOpen]);

    const lastSubmenuElement = navLink.menu_children?.[navLink.menu_children.length - 1];
    return (
        <div onClick={() => !isMobile && setIsDropdownOpen(true)}>
            <button
                className='text-[150%] lg:text-md flex w-full items-center justify-between gap-2 text-left py-5 lg:py-0 lg:flex-none lg:justify-start lg:px-4'
                onClick={() => setIsDropdownOpen((prev) => !prev)}
            >
                <span>{navLink.title}</span>
                <motion.span
                    variants={{
                        rotated: { rotate: 180 },
                        initial: { rotate: 0 },
                    }}
                    animate={isDropdownOpen ? "rotated" : "initial"}
                    transition={{ duration: 0.3 }}
                >
                    <RxChevronDown />
                </motion.span>
            </button>
            {isDropdownOpen && (
                <AnimatePresence>
                    <motion.nav
                        variants={{
                            open: {
                                visibility: "visible",
                                opacity: "var(--opacity-open, 100%)",
                                y: 0,
                            },
                            close: {
                                visibility: "hidden",
                                opacity: "var(--opacity-close, 0)",
                                y: 10,
                            },
                        }}
                        animate={isDropdownOpen ? "open" : "close"}
                        initial='close'
                        exit='close'
                        transition={{ duration: 0.3 }}
                        className={cn(
                            "lg:bg-opacity-[98%] lg:shadow-small lg:absolute w-full top-0 left-0 lg:pt-44 lg:pb-8 lg:z-[-1]",
                            "lg:bg-light",
                            smallNav === "small" &&
                                "lg:absolute lg:z-1 lg:pt-0 lg:top-16 lg:bg-[#EAECEFCC] lg:rounded-[18px] lg:min-w-max lg:pb-0"
                        )}
                    >
                        <div
                            className={cn(
                                "lg:container relative flex flex-col lg:grid grid-cols-1 lg:grid-cols-3 text-[120%] lg:text-[100%]",
                                smallNav === "small" && "lg:grid-cols-1 lg:p-8 lg:gap-4"
                            )}
                        >
                            {navLink.menu_children?.map(
                                (navLink: NavLinkProps, index: number, arr) => {
                                    const isLastElement = index === arr.length - 1;
                                    return (
                                        <React.Fragment key={index}>
                                            <div
                                                className={cn(
                                                    "flex flex-col gap-1",
                                                    isLastElement &&
                                                        smallNav != "small" &&
                                                        "col-span-4 lg:mt-8"
                                                )}
                                                key={index}
                                            >
                                                <Link
                                                    key={index}
                                                    href={navLink.url ?? "#"}
                                                    className={cn(
                                                        "flex flex-row items-center lg:flex-row lg:items-center gap-1 lg:text-h3 lg:mb-6 transition hover:text-brand-light",
                                                        isLastElement &&
                                                            smallNav != "small" &&
                                                            "flx-col lg:flex-row items-center gap-2 py-2 lg:text-[36px] transition",
                                                        smallNav === "small" &&
                                                            "lg:text-lg lg:mb-4 lg:flex-row lg:items-center lg:text-[36] lg:m-0"
                                                    )}
                                                >
                                                    <div>{parse(navLink.title)}</div>
                                                    {isLastElement && smallNav != "small" && (
                                                        <ArrowRight_Icon className='size-8 pt-2 flex-shrink-0' />
                                                    )}
                                                    {smallNav === "small" && (
                                                        <ArrowRight_Icon className='size-4 flex-shrink-0' />
                                                    )}
                                                </Link>
                                                {navLink.menu_children &&
                                                    navLink.menu_children?.map(
                                                        (child: any, key: number) => (
                                                            <Link
                                                                key={index}
                                                                href={child.url ?? "#"}
                                                                className='group text-stroke flex items-center lg:py-2 transition lg:text-[24px] hover:bg-brand hover:text-primary'
                                                            >
                                                                {parse(child.title)}
                                                                <ArrowRight_Icon className='size-4 flex-shrink-0 opacity-0 translate-x-0 group-hover:opacity-100 transition group-hover:translate-x-2' />
                                                            </Link>
                                                        )
                                                    )}

                                                <hr className='bg-primary bg-opacity-10 h-px border-0 my-4 lg:hidden' />
                                            </div>
                                        </React.Fragment>
                                    );
                                }
                            )}
                        </div>
                    </motion.nav>
                </AnimatePresence>
            )}
        </div>
    );
};
